<script setup>
/**
 * PassHeader — the consistent white app-chrome header shared across custom pass
 * layouts (NOT a module; the module system is retired for these layouts). It is
 * the cross-layout counterpart to PlaceDetailSheet / BrowseOverlay: any layout
 * mounts it at the top for a familiar, app-like header.
 *
 * White bar (brand logo + hamburger with an unread notification bubble) + a
 * time-based greeting with a compact weather chip (icon + temp) on the right.
 * Layout-agnostic: reads partnerUser/messages from the store, takes an optional
 * `logo` url override (else the offer/partner HorizontalLogo) and an optional
 * `unread` override. Emits `open-menu` so the host layout owns its own drawer.
 *
 * Greeting + weather logic is ported from the legacy `modules/Greeting`.
 */
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment'
import {
  Menu, Sun, Cloud, CloudRain, CloudDrizzle, CloudSnow, CloudLightning, CloudFog
} from '@lucide/vue'
import { getWithExpiry, setWithExpiry } from '@/helpers.js'

const props = defineProps({
  /** Brand logo url for the white bar. Falls back to the HorizontalLogo media. */
  logo: { type: String, default: null },
  /** Unread/attention count for the menu bubble. Falls back to messages.unread. */
  unread: { type: Number, default: null },
  /** Optional contextual line under the greeting. The host layout supplies the
      copy (e.g. today's featured business + the passholder's state), so this
      component stays layout-agnostic. */
  subline: { type: String, default: '' }
})
const emit = defineEmits(['open-menu'])

const store = useStore()
const partnerUser = computed(() => store.state.partnerUser || {})
const messages = computed(() => store.state.messages || {})

const resolvedLogo = computed(() => {
  if (props.logo) return props.logo
  const find = media => media?.find(m => m.location === 'HorizontalLogo' && m.url)?.url
  return find(store.state.offer?.media) || find(store.state.partner?.media) || null
})
const brandName = computed(() => store.state.partner?.name || 'Experiences')
const unreadCount = computed(() => props.unread ?? messages.value.unread ?? 0)

// Spruced-up greeting — fully universal: device clock (time-of-day) + the
// passholder's first_name (the only DB value, already loaded). Rotates an
// energetic opener per bucket with a subtle emoji; the opener + bucket are
// picked ONCE at setup so it stays stable through re-renders but varies per load.
const GREETING_OPENERS = {
  morning: ['Good morning', 'Morning', 'Rise & shine', 'Top of the morning'],
  afternoon: ['Good afternoon', 'Hey there', 'Afternoon', 'Hope your day is going great'],
  evening: ['Good evening', 'Evening', 'Hey there', 'Hope you had a great day']
}
const greetHour = Number(moment().format('HH'))
const greetBucket = greetHour >= 17 ? 'evening' : greetHour >= 12 ? 'afternoon' : 'morning'
const greetOpeners = GREETING_OPENERS[greetBucket]
const greetOpener = greetOpeners[Math.floor(Math.random() * greetOpeners.length)]
const greeting = computed(
  () => `${greetOpener}, ${partnerUser.value.first_name || 'Friend'}`
)

// Live local weather, condensed to an icon + temp. Ported from modules/Greeting.
const weather = ref(getWithExpiry('weather') || { loading: true })
const WEATHER_ICONS = {
  clear: Sun,
  clouds: Cloud,
  rain: CloudRain,
  drizzle: CloudDrizzle,
  thunderstorm: CloudLightning,
  snow: CloudSnow,
  mist: CloudFog,
  fog: CloudFog,
  haze: CloudFog,
  smoke: CloudFog
}
const weatherIcon = computed(() => WEATHER_ICONS[weather.value.currently] || Cloud)

const userLoc = computed(() => store.getters['userLocation/getUserLocation'])
const locationStatus = computed(() => store.getters['userLocation/getLocationStatus'])

function fetchWeather(latitude, longitude) {
  const KELVIN = 273
  const key = import.meta.env.DEV
    ? 'cbd5cf463384a7bdd5a147fd2ccfb001'
    : '6f6e6f09b733451cdaf897d769b42988'
  fetch(`https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&appid=${key}`)
    .then(res => res.json())
    .then(data => {
      if (data.cod === 401) throw data
      const us = ['US', 'VI'].includes(data.sys.country)
      const formatted = {
        temperature: us
          ? `${Math.round((9 / 5) * (data.main.temp - KELVIN) + 32)}º`
          : `${Math.round(data.main.temp - KELVIN)}º`,
        location: data.name,
        currently: data.weather[0].main.toLowerCase(),
        loading: false
      }
      weather.value = formatted
      setWithExpiry('weather', formatted)
    })
    .catch(() => { weather.value = { loading: false } })
}

watch(
  () => [userLoc.value.latitude, locationStatus.value],
  ([lat, status]) => {
    if (weather.value.temperature) return
    if (lat != null) fetchWeather(lat, userLoc.value.longitude)
    else if (status === 'denied') weather.value = { loading: false }
  },
  { immediate: true }
)
</script>

<template>
  <header class="ph">
    <!-- White bar: brand logo + optional tracker slot + hamburger (unread bubble) -->
    <div class="ph-bar">
      <img v-if="resolvedLogo" :src="resolvedLogo" :alt="brandName" class="ph-logo" />
      <span v-else class="ph-logo-text">{{ brandName }}</span>
      <div class="ph-bar__right">
        <!-- Layouts can drop a compact progress/tracker chip here. -->
        <slot name="tracker" />
        <button
          id="pass_menu_btn"
          type="button"
          class="ph-menu gtm-click"
          aria-label="Open menu"
          @click="emit('open-menu')"
        >
          <Menu :size="20" :stroke-width="2" />
        <span
          v-if="unreadCount > 0"
          class="ph-menu__badge"
          :aria-label="`${unreadCount} needing attention`"
        >{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
        </button>
      </div>
    </div>

    <!-- Greeting + compact weather chip (icon + temp), then optional context line -->
    <div class="ph-greeting">
      <div class="ph-greeting__row">
        <div class="ph-greeting__name">{{ greeting }}</div>
        <span
          v-if="weather.temperature"
          class="ph-weather"
          :aria-label="`${weather.currently}, ${weather.temperature}`"
        >
          <component :is="weatherIcon" :size="16" />
          {{ weather.temperature }}
        </span>
      </div>
      <p v-if="subline" class="ph-greeting__sub">{{ subline }}</p>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.ph {
  position: relative;
  z-index: 6;
  background: #fff;
  color: var(--fr-ink, #1c1c1e);
}

/* White bar — logo left, hamburger right; pads the notch since it's topmost. */
.ph-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(env(safe-area-inset-top) + 10px) 16px 8px;
}
.ph-bar__right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ph-logo {
  height: 22px;
  width: auto;
  max-width: 55%;
  object-fit: contain;
  display: block;
}
.ph-logo-text {
  font-family: var(--fr-font-serif-display, Georgia, serif);
  font-size: 16px;
  font-weight: 700;
}
.ph-menu {
  position: relative;
  display: grid;
  place-items: center;
  height: 44px;
  width: 44px;
  margin: -8px -10px -8px 0; /* 44px hit area without inflating the bar */
  border: 0;
  background: none;
  border-radius: 999px;
  color: rgba(28, 28, 30, 0.75);
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(0, 0, 0, 0.05); }
}
/* Notification bubble — anchored to the icon, not the 44px hit box. */
.ph-menu__badge {
  position: absolute;
  top: 6px;
  right: 6px;
  display: grid;
  place-items: center;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--fr-primary, #ff9900);
  color: var(--fr-primary-foreground, #fff);
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  box-shadow: 0 0 0 2px #fff; /* separates the bubble from the icon */
}

/* Greeting block — a row (name + weather) with an optional context line below. */
.ph-greeting {
  padding: 8px 16px 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.ph-greeting__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ph-greeting__sub {
  margin: 3px 0 0;
  font-size: 12px;
  line-height: 1.35;
  color: var(--fr-muted, #6b7280);
}
.ph-greeting__name {
  min-width: 0;
  font-family: var(--fr-font-serif-display, Georgia, serif);
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ph-weather {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--fr-muted, #6b7280);
  :deep(svg) { color: var(--fr-primary, #ff9900); }
}
</style>
